import { canonicalise } from './canonical.js';
import { createNotes, dedent, deriveDescription, frontmatterBlock, quoteAttr, reportUnmappedFrontmatter, splitFrontmatter, } from './import-notes.js';
import { fenceWidth, findJsxElements, replaceJsxElements, replaceOutsideCode, } from './import-scan.js';
import { convertDetails, convertMdxComments, convertUnmodelledComponents, convertMdxDocumentImports, hideMdxStatements, wrapCodeMeta, } from './import-mdx.js';
/**
 * Convert a Mintlify page into our canonical Markdown.
 *
 * Mintlify is the one with the most to convert, because it is the one that puts
 * the most of a page inside components. A Mintlify page is MDX where the
 * callouts, the procedures, the tabs, the accordions, the card grids and the
 * screenshots are all JSX elements, so an importer that only handled Markdown
 * would produce a page of unstyled fragments.
 *
 * Six of those components map exactly onto blocks we already have, which is the
 * reason this is worth doing:
 *
 *   <Note> <Info> <Tip> <Warning> <Danger>  ->  hint
 *   <Steps> / <Step title>                  ->  steps / step
 *   <Tabs> / <Tab title>                    ->  tabs / tab
 *   <Accordion title> / <Expandable title>  ->  expandable
 *   <Card title href> / <CardGroup>         ->  card / cards
 *   <Frame caption>                          ->  figure
 *   <CodeGroup>                              ->  tabs of code
 *
 * HONESTY ABOUT THE EVIDENCE. The fixtures in `test/fixtures-mintlify` are
 * SYNTHETIC — written from Mintlify's documented components, not exported from a
 * real site. They prove each transform is correct and lossless on the syntax they
 * cover. They are not a count, and the GitBook importer is the reminder of why
 * that matters: counting 25 real pages changed two decisions there.
 *
 * What we DO know, and what this file reports rather than hides:
 *
 * - Mintlify's API reference components have no counterpart at all.
 *   `<ResponseField>`, `<ParamField>`, `<RequestExample>` and `<ResponseExample>`
 *   are the structure of an API reference page, and we have no API reference
 *   block. They import as directives that keep every attribute and every child,
 *   so nothing is lost and nothing renders properly yet. A real Mintlify
 *   customer with an API reference cannot be migrated on this alone, and the
 *   report says so on every page that has one.
 * - `<Snippet file="…" />` is an include, and now becomes one of ours: the
 *   reference is carried and the import service copies the referenced files in
 *   as shared blocks. The MDX-import form (`import X from '/snippets/x.mdx'`
 *   then `<X />`) still cannot be resolved without evaluating the module, so
 *   it is reported rather than guessed at.
 * - `<Check>` is a green success callout. Our scale has no success level, so it
 *   becomes `tip` — the same decision the GitBook importer made for GitBook's
 *   `success`, and reported the same way.
 * - `<CardGroup cols>`, `<AccordionGroup>`, an `icon` on anything, and
 *   `mode: wide` are all layout. We decide layout, so they are reported and
 *   dropped rather than half-honoured.
 */
/** Mintlify callouts, mapped onto our hint scale. */
const CALLOUT = {
    Note: { type: 'note', exact: true },
    Info: { type: 'info', exact: true },
    Tip: { type: 'tip', exact: true },
    Warning: { type: 'warning', exact: true },
    Danger: { type: 'danger', exact: true },
    Caution: { type: 'warning', exact: false },
    Check: { type: 'tip', exact: false },
};
/**
 * The API reference components we still have no block for.
 *
 * The fields and the examples convert; these three do not, and each fails for
 * its own reason: `<ResponseExamples>` is a panel of many responses keyed by
 * status code, `<OpenApi>` generates the page from a spec file we do not read,
 * and `<SdkSnippet>` pulls text from Mintlify's own service. Listed by name so
 * the report can say what is missing once, rather than leaving the reviewer to
 * infer it from a list of unconverted component names.
 */
const API_COMPONENTS = ['ResponseExamples', 'OpenApi', 'SdkSnippet'];
/** Frontmatter keys this importer reads. Anything else is reported. */
const KNOWN_FRONTMATTER = ['title', 'sidebarTitle', 'description', 'icon', 'mode', 'noindex'];
export function importMintlifyPage(source, options = {}) {
    const { note, list } = createNotes();
    const { data: frontmatter, body } = splitFrontmatter(source.replace(/\r\n/g, '\n'), note);
    // ---- 1. frontmatter ----------------------------------------------------
    const hints = {};
    let title = str(frontmatter['title']) ?? '';
    let description = str(frontmatter['description']);
    const sidebarTitle = str(frontmatter['sidebarTitle']);
    if (sidebarTitle) {
        hints.navLabel = sidebarTitle;
        note('review', 'sidebar-title', 'A `sidebarTitle` cannot live in our frontmatter. It was carried into the navigation proposal, where labels belong.');
    }
    if (str(frontmatter['icon'])) {
        note('unconverted', 'page-icon', 'A page `icon` was not carried over. Mintlify icons are Font Awesome names; our navigation icons are single characters in nav.yaml, so the name cannot be reused as it stands.');
    }
    if (str(frontmatter['mode'])) {
        note('unconverted', 'page-mode', 'A page `mode` (wide, custom, center) chose a Mintlify layout. Layout is ours to decide, so the setting was not carried over.');
    }
    if (frontmatter['noindex'] === true) {
        note('unconverted', 'noindex', 'The page asked search engines not to index it. We have no per-page noindex, so the page imports as indexable.');
    }
    reportUnmappedFrontmatter(frontmatter, KNOWN_FRONTMATTER, note);
    // ---- 2. MDX that is code, not prose ------------------------------------
    /*
     * Before the statements are hidden, because a document import is an
     * INCLUDE rather than a component: resolving it turns both the import and
     * its use into one shared block, and hiding the statement first would
     * leave the reference behind with nothing to point at.
     */
    let markdown = convertMdxDocumentImports(body, note);
    markdown = hideMdxStatements(markdown, note, 'Mintlify');
    markdown = convertMdxComments(markdown, note);
    // Carried first: what is left for `reportSnippets` to complain about is the
    // MDX-import form, which names a component rather than a file and cannot be
    // resolved without evaluating the module.
    markdown = convertSnippetTags(markdown, note);
    reportSnippets(markdown, note);
    reportApiComponents(markdown, note);
    // ---- 3. components we model, innermost first --------------------------
    markdown = convertComponents(markdown, note);
    markdown = convertDetails(markdown, note);
    /**
     * ---- 4. code fences, AFTER the containers ----------------------------
     *
     * This used to run FIRST, and the reason was load-bearing: a container's fence
     * must be wider than every fence inside it, and the old code pass WRAPPED a
     * titled fence in a `:::code` directive. Converting `<Tab>` first measured its
     * body while the code inside was still a plain fence, so the tab got `:::` —
     * then the wrapper made the two equal, the code block's fence closed the tab,
     * and half of each tab fell out of the block.
     *
     * Nothing is wrapped now: the metastring stays on the fence and no `:::` is
     * added, so no container's width can be matched by it. The constraint is gone,
     * and running second fixes what running first broke — `<CodeGroup>` names each
     * tab from its fence's metastring, and consuming that metastring before the
     * group was converted left every tab untitled.
     */
    markdown = wrapCodeMeta(markdown, note, CODE_META);
    // ---- 5. paths ----------------------------------------------------------
    markdown = rewritePaths(markdown, note);
    // ---- 6. every component left ------------------------------------------
    markdown = convertUnmodelledComponents(markdown, note);
    // ---- 7. title ----------------------------------------------------------
    markdown = markdown.replace(/^# +(.+?)\s*$\n?/m, (all, heading) => {
        const found = heading.replace(/\*\*/g, '').trim();
        if (!title) {
            title = found;
            return '';
        }
        if (found.toLowerCase() === title.toLowerCase()) {
            note('removed', 'duplicate-h1', 'The page repeated its frontmatter title as an H1. Our site renders the title once, from frontmatter, so the H1 was removed.');
            return '';
        }
        return all;
    });
    if (!title) {
        note('review', 'no-title', 'No frontmatter title and no H1. The title needs to be set by hand.');
    }
    // ---- 8. description ---------------------------------------------------
    let finalBody = markdown.trim();
    if (!description && !options.sharedBlock) {
        const derived = deriveDescription(finalBody, note);
        description = derived.description;
        finalBody = derived.body;
    }
    return {
        title,
        ...(description ? { description } : {}),
        markdown: canonicalise(`${frontmatterBlock({ title, ...(description ? { description } : {}) })}\n${finalBody.trim()}\n`),
        notes: list(),
        ...(Object.keys(hints).length > 0 ? { hints } : {}),
    };
}
/** The page id a Mintlify file imports to. Its paths ARE its URLs. */
export function mintlifyPageId(path) {
    const id = path.replace(/\.mdx?$/, '').replace(/^\/+/, '');
    return id === '' ? 'index' : id;
}
/**
 * `<Snippet file="…" />` becomes our own snippet reference.
 *
 * This was the one construct the importer could not preserve, because the
 * content genuinely was not in the file — it lived in another one. Now that
 * this product has snippets of its own, the reference can be carried and the
 * import service copies the referenced FILES in beside it, so the words
 * arrive with the page that shows them.
 *
 * The name is derived from the path, because that is the only stable
 * identifier both halves of the import can compute independently.
 */
export function snippetNameFromPath(path) {
    return (path
        .replace(/^\/+/, '')
        .replace(/^snippets\//, '')
        .replace(/\.mdx?$/, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'snippet');
}
function convertSnippetTags(text, note) {
    let carried = 0;
    const out = text.replace(/<Snippet\s+file=(?:"([^"]+)"|'([^']+)')\s*\/?>(?:\s*<\/Snippet>)?/g, (_match, dq, sq) => {
        carried += 1;
        return `::snippet{name="${snippetNameFromPath(dq ?? sq ?? '')}"}`;
    });
    if (carried > 0) {
        note('review', 'snippet-carried', `${carried} snippet reference(s) became shared blocks, and the files they name were imported alongside the pages. Check the shared block reads correctly where it appears — Mintlify snippets sometimes rely on the surrounding page for context.`);
    }
    return out;
}
/** `<Snippet>` and `/snippets` imports: content that is not in the file. */
function reportSnippets(text, note) {
    const uses = (text.match(/<Snippet\b/g) ?? []).length + (text.match(/\/snippets\//g) ?? []).length;
    if (uses === 0)
        return;
    note('unconverted', 'snippet', 'The page included a snippet from another file. We have no include mechanism, so the included TEXT IS NOT ON THIS PAGE — this is the one construct the importer cannot even preserve, because the content was never in the file. Each one has to be pasted in or replaced.', uses);
}
/** Say once, per page, that this is an API reference. */
function reportApiComponents(text, note) {
    const found = API_COMPONENTS.filter((name) => new RegExp(`<${name}\\b`).test(text));
    if (found.length === 0)
        return;
    note('unconverted', 'api-reference-page', `This page uses ${found.join(', ')}, which we do not model. Parameter and response fields convert; these do not, because each needs something we do not have: a status-code panel, the OpenAPI spec file itself, or Mintlify's snippet service.`);
}
/**
 * Convert every component we model, INNERMOST FIRST.
 *
 * One recursive pass, not one pass per component, and the reason is the fence
 * width. A directive's fence has to be wider than everything inside it, so a
 * container cannot be built until its children are in their final form. The
 * first version of this file ran one pass per component — frames, then steps,
 * then tabs, then callouts — and a `<Tab>` holding a `<Warning>` was measured
 * while the warning was still JSX. The tab got `:::`, the warning later became
 * `:::hint`, and the two being equal meant the hint's fence CLOSED the tab.
 * Everything after it in that tab fell out of the block.
 *
 * So: recurse into the children, convert them, then measure. The recursion also
 * removes the ordering question entirely — no arrangement of the passes could
 * have been correct, because components nest both ways round.
 */
const MODELLED = new Set([
    'Frame',
    'Steps',
    'Tabs',
    'CodeGroup',
    'CardGroup',
    'Card',
    'AccordionGroup',
    'Accordion',
    'Expandable',
    'ParamField',
    'ResponseField',
    'RequestExample',
    'ResponseExample',
    ...Object.keys(CALLOUT),
]);
function convertComponents(text, note) {
    return replaceJsxElements(text, (name) => MODELLED.has(name), (element) => {
        switch (element.name) {
            case 'ParamField':
            case 'ResponseField':
                return fieldToField(element, note);
            case 'RequestExample':
            case 'ResponseExample':
                return exampleToCode(element, note);
            case 'Frame':
                return frameToFigure(element, note);
            case 'Steps':
                return stepsToSteps(element, note);
            case 'Tabs':
                return tabsToTabs(element, note);
            case 'CodeGroup':
                return codeGroupToTabs(element, note);
            case 'CardGroup':
                return cardGroupToCards(element, note);
            case 'Card':
                return loneCardToCards(element, note);
            case 'AccordionGroup':
                return accordionGroupToExpandables(element, note);
            case 'Accordion':
            case 'Expandable':
                return accordionToExpandable(element, note);
            default:
                return calloutToHint(element, note);
        }
    });
}
/**
 * An element's children, dedented, code-wrapped, and fully converted.
 *
 * The code wrapping has to happen HERE and not only at the top of the page.
 * MEASURED, from the fixtures: JSX children are indented, a Markdown code fence
 * indented four spaces or more is not a fence at all, so a page-level pass never
 * sees the fences inside a `<Tab>`. It found nothing, the children were dedented
 * afterwards, and the `title="publish.js"` on that fence was then thrown away by
 * canonicalise — a silent loss of exactly the kind this importer exists to
 * prevent. Wrapping is idempotent, so doing it again per level is safe.
 */
function inner(element, note) {
    // The unmodelled pass belongs INSIDE the recursion too. An `<Expandable>`
    // holding two `<ResponseField>` elements measured its body while those were
    // still JSX, so it took `:::` — and the fields later became `:::response-field`
    // at the same width, which closed the expandable early and left the second
    // field outside it. Everything the body will become has to exist before the
    // body is measured, whether we model it or not.
    return convertUnmodelledComponents(convertComponents(prepare(element.children, note), note), note);
}
/** Dedent, then wrap any code fence that carries a metastring. */
function prepare(children, note) {
    return wrapCodeMeta(dedent(children), note, CODE_META);
}
/**
 * Mintlify's metastring, in the renderer's spelling.
 *
 * `lines` is Mintlify's word for what Expressive Code calls `showLineNumbers`.
 * `wrap` is the same word in both. `expandable` has no equivalent and is kept as
 * written — the renderer ignores it rather than failing, so keeping it loses
 * nothing and deleting it would lose the author's intent.
 *
 * `bareTitle` because Mintlify writes the filename with no `title=`:
 * ```` ```javascript App.js ````
 */
const CODE_META = {
    flags: { lines: 'showLineNumbers', wrap: 'wrap', expandable: 'expandable' },
    bareTitle: true,
};
/** `<Frame caption="...">` onto `figure`. */
function frameToFigure(element, note) {
    const pair = findThemePair(element.children);
    const image = pair?.light ?? findImage(element.children);
    if (!image) {
        note('unconverted', 'frame-without-image', 'A `<Frame>` held no image the scanner could find, so it was left for the component fallback. A figure with no image source is not a figure.');
        return undefined;
    }
    const caption = element.attributes['caption'] ?? '';
    const parts = [`src="${quoteAttr(image.src)}"`, `alt="${quoteAttr(image.alt)}"`];
    if (image.width)
        parts.push(`width="${quoteAttr(image.width)}"`);
    if (pair) {
        parts.push(`srcDark="${quoteAttr(pair.dark.src)}"`);
        note('review', 'frame-theme-pair', 'A `<Frame>` held two images, one hidden in each theme by Tailwind classes. They became ONE figure with a dark source: the reader sees the right picture, and the page no longer shows both stacked.');
    }
    if (image.alt.trim() === '') {
        note('review', 'image-no-alt', 'An image has no alt text. A screen reader announces nothing for it, and the source had none to carry over.');
    }
    note('review', 'frame', '`<Frame>` became `:::figure`, with its `caption` as the figure caption. Frame styling — the border, the background, the dark-mode variant — is ours to decide and was not carried over.');
    const fence = ':'.repeat(fenceWidth(caption));
    return `${fence}figure{${parts.join(' ')}}\n${caption}\n${fence}`;
}
/**
 * Two images, one for each theme.
 *
 * Mintlify draws a light/dark pair as two `<img>` tags with Tailwind classes:
 * one carries `dark:hidden`, the other `hidden dark:block`. Imported as two
 * images, a page shows BOTH, stacked — which is visibly wrong on every page
 * that has one, and was the state of every imported screenshot.
 *
 * Matched on the CLASSES rather than on order, because the order varies
 * between pages and between people, and `dark:hidden` on the first is the one
 * thing every example agrees on.
 *
 * Only a pair. Three images, or two that are not a light/dark pair, fall
 * through to the ordinary single-image path — guessing which two of three
 * belong together would be inventing a relationship the author did not write.
 */
function findThemePair(text) {
    const tags = [...text.matchAll(/<img\b[^>]*>/g)].map((m) => m[0]);
    if (tags.length !== 2)
        return undefined;
    const classOf = (tag) => tag
        .match(/class(?:Name)?=(?:"([^"]*)"|'([^']*)')/)
        ?.slice(1)
        .find(Boolean) ?? '';
    const hidesInDark = (tag) => /\bdark:hidden\b/.test(classOf(tag));
    const showsInDark = (tag) => /\bdark:block\b|\bdark:inline\b/.test(classOf(tag));
    const light = tags.find(hidesInDark);
    const dark = tags.find(showsInDark);
    if (!light || !dark || light === dark)
        return undefined;
    const lightImage = findImage(light);
    const darkImage = findImage(dark);
    if (!lightImage || !darkImage)
        return undefined;
    return { light: lightImage, dark: { src: darkImage.src } };
}
/** An `<img>` or a Markdown image inside a block. */
function findImage(text) {
    const tag = /<img\b([^>]*)>/.exec(text);
    if (tag) {
        const attr = (name) => tag[1]
            .match(new RegExp(`${name}=(?:"([^"]*)"|'([^']*)')`))
            ?.slice(1)
            .find(Boolean);
        const src = attr('src');
        if (src) {
            const width = attr('width');
            return { src, alt: attr('alt') ?? '', ...(width ? { width } : {}) };
        }
    }
    const markdown = /!\[([^\]]*)\]\(([^)\s]+)[^)]*\)/.exec(text);
    if (markdown)
        return { src: markdown[2], alt: markdown[1] };
    return undefined;
}
/** `<Steps>` / `<Step title>` onto our numbered procedure. */
function stepsToSteps(element, note) {
    const steps = findJsxElements(element.children, (name) => name === 'Step');
    if (steps.length === 0) {
        note('unconverted', 'steps-without-steps', 'A `<Steps>` element had no `<Step>` children the scanner could read. It was left as written rather than guessed at.');
        return undefined;
    }
    const rendered = steps.map((step) => {
        const body = inner(step, note);
        for (const attribute of ['icon', 'stepNumber', 'titleSize']) {
            if (step.attributes[attribute] === undefined && step.expressions[attribute] === undefined) {
                continue;
            }
            note('unconverted', 'step-presentation', 'A `<Step>` set an `icon`, a `stepNumber` or a `titleSize`. Our steps are numbered by the list itself and styled by us, so those were not carried over.');
        }
        const fence = ':'.repeat(fenceWidth(body));
        const title = step.attributes['title'];
        return `${fence}step${title ? `{title="${quoteAttr(title)}"}` : ''}\n${body}\n${fence}`;
    });
    note('review', 'steps', '`<Steps>` became `::::steps`, with one `:::step` per `<Step>`.');
    const joined = rendered.join('\n\n');
    const outer = ':'.repeat(fenceWidth(joined));
    return `${outer}steps\n${joined}\n${outer}`;
}
/** `<Tabs>` / `<Tab title>`. */
function tabsToTabs(element, note) {
    const tabs = findJsxElements(element.children, (name) => name === 'Tab');
    if (tabs.length === 0) {
        note('unconverted', 'tabs-without-tabs', 'A `<Tabs>` element had no `<Tab>` children the scanner could read. It was left as written rather than guessed at.');
        return undefined;
    }
    const rendered = tabs.map((tab) => {
        const body = inner(tab, note);
        const fence = ':'.repeat(fenceWidth(body));
        return `${fence}tab{title="${quoteAttr(tab.attributes['title'] ?? 'Tab')}"}\n${body}\n${fence}`;
    });
    note('review', 'tabs', '`<Tabs>` became `::::tabs`, with one `:::tab` per `<Tab>`.');
    const joined = rendered.join('\n\n');
    const outer = ':'.repeat(fenceWidth(joined));
    return `${outer}tabs\n${joined}\n${outer}`;
}
/**
 * `<CodeGroup>` onto tabs of code.
 *
 * The tab titles come from each fence's `title=` metastring, which is where
 * Mintlify puts them — and the fence keeps that metastring, so the tab shows the
 * filename on the block as well as on the tab, exactly as the source site did.
 */
function codeGroupToTabs(element, note) {
    const tabs = [];
    for (const block of codeBlocks(prepare(element.children, note))) {
        if (block.title === undefined) {
            note('review', 'code-group-untitled', 'A code block inside a `<CodeGroup>` had no `title`, so its tab is named after its language.');
        }
        const fence = ':'.repeat(fenceWidth(block.text));
        tabs.push(`${fence}tab{title="${quoteAttr(block.title ?? block.language ?? 'Code')}"}\n${block.text}\n${fence}`);
    }
    if (tabs.length === 0) {
        note('unconverted', 'code-group-empty', 'A `<CodeGroup>` held no code blocks the scanner could read, so it was left as written.');
        return undefined;
    }
    note('review', 'code-group', '`<CodeGroup>` became `::::tabs`, one tab per code block, with the titles taken from each fence.');
    const joined = tabs.join('\n\n');
    const outer = ':'.repeat(fenceWidth(joined));
    return `${outer}tabs\n${joined}\n${outer}`;
}
/**
 * The code blocks inside a `<CodeGroup>`, in order.
 *
 * The title comes off the FENCE, which is where Mintlify writes it and where it
 * now stays. This used to look for a `:::code{title=...}` wrapper, because the
 * code pass produced one; when the wrapper went away it found nothing, and every
 * tab in a `<CodeGroup>` was named after its language instead of its filename.
 *
 * The block is returned whole, fence included, so the tab keeps the metastring
 * and renders its filename.
 */
function codeBlocks(body) {
    const blocks = [];
    const lines = body.split('\n');
    let i = 0;
    while (i < lines.length) {
        const fence = /^([ \t]*)(`{3,}|~{3,})(.*)$/.exec(lines[i]);
        if (fence) {
            const collected = [lines[i]];
            i++;
            while (i < lines.length && !new RegExp(`^[ \t]*${fence[2]}[ \t]*$`).test(lines[i])) {
                collected.push(lines[i++]);
            }
            if (i < lines.length)
                collected.push(lines[i++]);
            const info = fence[3].trim();
            const language = info.match(/^[A-Za-z0-9+#._-]*/)?.[0];
            // Quoted first — Mintlify writes `title="npm"`, and the code pass has
            // normalised a bare FILENAME into that form by the time this runs.
            const quoted = info
                .match(/title=(?:"([^"]*)"|'([^']*)')/)
                ?.slice(1)
                .find(Boolean);
            const bare = quoted ? undefined : bareLabel(info.slice(language?.length ?? 0));
            let text = collected.join('\n');
            // The label has been claimed as the tab's name, so it comes off the
            // fence. Left on, the renderer would print `cURL` as a word inside the
            // code block's header, next to the tab that already says it.
            if (bare)
                text = text.replace(bare, '').replace(/[ \t]+$/m, '');
            const title = quoted ?? bare;
            blocks.push({
                ...(title ? { title } : {}),
                ...(language ? { language } : {}),
                text,
            });
            continue;
        }
        i++;
    }
    return blocks;
}
/**
 * The one word after the language that names a tab.
 *
 * Mintlify labels the tabs of a `<CodeGroup>` with a bare word on the fence:
 *
 * ```` ```bash cURL ````
 *
 * The general code pass deliberately will NOT read that — outside a group a
 * stray word is a stray word, and turning it into a header would put a made-up
 * caption on somebody's code. Inside a group the same word is the label, by
 * Mintlify's own documentation, so this reads it and only this does.
 *
 * Everything the renderer already understands is skipped, or every code block
 * with line numbers would be named "showLineNumbers". One word must be left
 * over: two words is not a label we can pick between.
 */
function bareLabel(rest) {
    const words = rest
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0)
        .filter((word) => !RENDERER_META.has(word))
        .filter((word) => !word.startsWith('{') && !word.includes('='));
    return words.length === 1 ? words[0] : undefined;
}
/** Metastring words the renderer reads, which are therefore not labels. */
const RENDERER_META = new Set([
    ...Object.values(CODE_META.flags),
    ...Object.keys(CODE_META.flags),
    'twoslash',
    'collapse',
]);
/** `<CardGroup>` onto our card grid. */
function cardGroupToCards(element, note) {
    const cards = findJsxElements(element.children, (name) => name === 'Card');
    if (cards.length === 0) {
        note('unconverted', 'card-group-empty', 'A `<CardGroup>` held no `<Card>` children the scanner could read, so it was left as written.');
        return undefined;
    }
    if (element.attributes['cols'] !== undefined || element.expressions['cols'] !== undefined) {
        note('unconverted', 'card-group-columns', 'A `<CardGroup cols>` set the number of columns. Our card grid decides its own columns from the space available, so the setting was not carried over.');
    }
    const joined = cards.map((card) => renderCard(card, note)).join('\n\n');
    const outer = ':'.repeat(fenceWidth(joined));
    note('review', 'card-group', '`<CardGroup>` became `::::cards`, with one `:::card` per `<Card>`.');
    return `${outer}cards\n${joined}\n${outer}`;
}
/** A `<Card>` on its own still needs a grid: our card only exists inside one. */
function loneCardToCards(element, note) {
    const card = renderCard(element, note);
    const outer = ':'.repeat(fenceWidth(card));
    note('review', 'card', 'A `<Card>` outside a `<CardGroup>` became a one-card `::::cards` grid, because our card only exists inside a grid.');
    return `${outer}cards\n${card}\n${outer}`;
}
function renderCard(element, note) {
    const attributes = [];
    const title = element.attributes['title'];
    if (title)
        attributes.push(`title="${quoteAttr(title)}"`);
    const href = element.attributes['href'];
    if (href)
        attributes.push(`href="${quoteAttr(href)}"`);
    if (element.attributes['icon']) {
        note('unconverted', 'card-icon', 'A `<Card icon>` used a Font Awesome name. Our card icon is a single character in the page source, so the name was not carried over.');
    }
    for (const attribute of ['img', 'horizontal', 'color', 'cta', 'arrow']) {
        if (element.attributes[attribute] === undefined &&
            element.expressions[attribute] === undefined) {
            continue;
        }
        note('unconverted', 'card-presentation', 'A `<Card>` set an image, a colour, a call to action or the horizontal layout. Our card renders a title, a link and its content, so those were not carried over.');
    }
    const body = inner(element, note);
    const fence = ':'.repeat(fenceWidth(body));
    const attrs = attributes.length > 0 ? `{${attributes.join(' ')}}` : '';
    return body.trim().length > 0
        ? `${fence}card${attrs}\n${body}\n${fence}`
        : `${fence}card${attrs}\n${fence}`;
}
/**
 * An `<AccordionGroup>` is unwrapped.
 *
 * We have no group block, so its accordions become a plain sequence. They still
 * open and close; they no longer share a frame or close each other.
 */
function accordionGroupToExpandables(element, note) {
    note('unconverted', 'accordion-group', 'An `<AccordionGroup>` was unwrapped: we have no group block, so its accordions became a plain sequence of `:::expandable` blocks. They still open and close; they no longer share a frame or close each other.');
    return inner(element, note);
}
/** `<Accordion>` and `<Expandable>` onto `expandable`. */
function accordionToExpandable(element, note) {
    const parts = [];
    const description = element.attributes['description'];
    if (description) {
        note('review', 'accordion-description', 'An `<Accordion description>` was a subtitle beside the title. Our expandable has a title only, so the description became an italic first line inside it.');
        parts.push(`_${description.replace(/[_*]/g, '')}_`);
    }
    const body = inner(element, note);
    if (body.trim().length > 0)
        parts.push(body);
    if (element.attributes['defaultOpen'] !== undefined ||
        element.expressions['defaultOpen'] !== undefined) {
        note('unconverted', 'accordion-default-open', 'An accordion opened by default. Our expandable always starts closed.');
    }
    if (element.attributes['icon']) {
        note('unconverted', 'accordion-icon', 'An accordion `icon` used a Font Awesome name, which our expandable has nowhere to put.');
    }
    note('review', 'accordion', `\`<${element.name}>\` became \`:::expandable\`. Both are a title with content that opens and closes.`);
    const joined = parts.join('\n\n');
    const fence = ':'.repeat(fenceWidth(joined));
    const title = element.attributes['title'] ?? 'Details';
    return `${fence}expandable{title="${quoteAttr(title)}"}\n${joined}\n${fence}`;
}
/**
 * Where a `<ParamField>` sits, in Mintlify's spelling.
 *
 * Mintlify writes the location and the name as ONE attribute — `query="limit"`
 * means a query parameter called `limit` — so the location cannot be read
 * without knowing which keys are locations. `<ResponseField>` has no location:
 * a response body has no query string.
 */
const FIELD_LOCATION = ['query', 'path', 'body', 'header'];
/** `<ParamField>` and `<ResponseField>` onto `field`. */
function fieldToField(element, note) {
    const attrs = {};
    const where = FIELD_LOCATION.find((key) => element.attributes[key] !== undefined);
    const name = where ? element.attributes[where] : (element.attributes['name'] ?? '');
    if (name)
        attrs['name'] = name;
    if (where)
        attrs['in'] = where;
    const type = element.attributes['type'];
    if (type)
        attrs['type'] = type;
    if (element.attributes['required'] !== undefined || element.expressions['required'] !== undefined)
        attrs['required'] = 'true';
    const fallback = element.attributes['default'];
    if (fallback)
        attrs['default'] = fallback;
    if (element.attributes['placeholder'] || element.attributes['initialValue']) {
        note('unconverted', 'field-playground', "A field carried `placeholder` or `initialValue`. Those fill in Mintlify's live API playground, which we do not have, so they were dropped. The field itself still documents the parameter.");
    }
    if (element.attributes['deprecated'] !== undefined) {
        note('review', 'field-deprecated', 'A field was marked `deprecated`. We have no deprecated flag on a field, so say it in the description — nothing else records it.');
    }
    if (!name) {
        note('unconverted', 'field-unnamed', `A <${element.name}> had no name: no \`name\` attribute and no ${FIELD_LOCATION.join('/')} attribute. The field imports with its description and no name.`);
    }
    const body = inner(element, note);
    const fence = ':'.repeat(fenceWidth(body));
    const written = Object.entries(attrs)
        .map(([key, value]) => `${key}="${quoteAttr(value)}"`)
        .join(' ');
    note('review', 'field', `\`<${element.name}>\` became \`:::field\`, which is the same thing: a name, a type, whether it is required, and the prose.`);
    return `${fence}field${written ? `{${written}}` : ''}\n${body}\n${fence}`;
}
/**
 * `<RequestExample>` and `<ResponseExample>` onto plain code.
 *
 * Mintlify pins these to a panel down the right of the page. We have no such
 * panel and inventing one for imported pages only would be a block nobody could
 * author. So the code lands where it was written, in reading order, and keeps
 * its label: an untitled block is titled "Request" or "Response", which is what
 * the panel's heading said. Several blocks become tabs, the same shape a
 * `<CodeGroup>` takes, because that is what several examples of one call are.
 */
function exampleToCode(element, note) {
    const label = element.name === 'RequestExample' ? 'Request' : 'Response';
    const blocks = codeBlocks(prepare(element.children, note));
    if (blocks.length === 0) {
        note('unconverted', 'api-example-empty', `A \`<${element.name}>\` held no code blocks the scanner could read, so it was left as written.`);
        return undefined;
    }
    note('review', 'api-example', `\`<${element.name}>\` was a panel pinned beside the page. We have no side panel, so its code moved inline, where it was written, under the name "${label}".`);
    const titled = blocks.map((block) => (block.title === undefined ? retitle(block, label) : block));
    if (titled.length === 1)
        return titled[0].text;
    const tabs = titled
        .map((block) => {
        const fence = ':'.repeat(fenceWidth(block.text));
        return `${fence}tab{title="${quoteAttr(block.title ?? label)}"}\n${block.text}\n${fence}`;
    })
        .join('\n\n');
    const outer = ':'.repeat(fenceWidth(tabs));
    return `${outer}tabs\n${tabs}\n${outer}`;
}
/** Name an untitled code block after the panel it came out of. */
function retitle(block, label) {
    const at = block.text.indexOf('\n');
    const open = at === -1 ? block.text : block.text.slice(0, at);
    const rest = at === -1 ? '' : block.text.slice(at);
    return { ...block, title: label, text: `${open.trimEnd()} title="${label}"${rest}` };
}
/** `<Note>`, `<Warning>`, `<Tip>`, `<Info>`, `<Check>`, `<Danger>` onto `hint`. */
function calloutToHint(element, note) {
    const mapping = CALLOUT[element.name];
    if (!mapping.exact) {
        note('review', `callout-${element.name.toLowerCase()}`, element.name === 'Check'
            ? "`<Check>` is a green success callout and our scale has no success level, so it became `tip` — the same decision the GitBook importer made for GitBook's `success`. A reader of the old site saw a tick, not a lightbulb."
            : `\`<${element.name}>\` has no exact counterpart on our scale and became the nearest one.`);
    }
    const body = inner(element, note);
    const fence = ':'.repeat(fenceWidth(body));
    note('review', 'callout', 'Mintlify callout components became `:::hint` blocks.');
    return `${fence}hint{type="${mapping.type}"}\n${body}\n${fence}`;
}
/** Link suffixes, and the assets that cannot resolve after a move. */
function rewritePaths(text, note) {
    let out = replaceOutsideCode(text, /\]\(([^)\s]+?)\.mdx?(?=[)#])/g, (match) => {
        const target = match[1];
        if (/^[a-z][a-z0-9+.-]*:/i.test(target))
            return undefined;
        note('review', 'link-suffix', 'Stripped the `.mdx` suffix from links between pages. Mintlify serves a page at its path without the extension, and so do we.');
        return `](${target}`;
    });
    const assets = out.match(/(?:\]\(|src=")(\/(?:images|logo|snippets|assets)\/)[^)"]*/g);
    if (assets) {
        note('unconverted', 'asset-path', 'Links to files served from the old site root (`/images/...`, `/logo/...`). The importer resolves each one against the export and repoints it at the asset store; the report names the ones the export does not contain, and those stay pointing at the old site.', assets.length);
    }
    const images = out.match(/<img\b[^>]*>/g);
    if (images) {
        note('review', 'html-image', 'Standalone `<img>` tags were left as HTML. They render, but a `className` on one does nothing here — and a light/dark pair of images, which Mintlify does with `dark:hidden`, now shows BOTH images.', images.length);
    }
    return out;
}
// ------------------------------------------------------------- navigation
/**
 * Read `docs.json`, or the legacy `mint.json`, into a navigation proposal.
 *
 * Both are JSON, so unlike the Docusaurus and VitePress readers this one is a
 * parse rather than a scan. The shapes differ: `mint.json` has a flat list of
 * groups, and `docs.json` nests groups under tabs, and tabs under anchors,
 * dropdowns, languages or versions. Those upper levels have no equivalent in
 * nav.yaml — a tab is not a section and not a space — so a tab becomes a section
 * whose items are its groups, which keeps every page reachable and every
 * grouping visible, one level deeper than the source had it.
 */
export function mintlifyNav(source) {
    const { note, list } = createNotes();
    let config;
    try {
        config = JSON.parse(source);
    }
    catch {
        note('unconverted', 'config-unparseable', 'docs.json could not be parsed as JSON. The navigation was ignored and the directory tree used instead.');
        return { source: 'the directory tree', sections: [], notes: list() };
    }
    if (config === null || typeof config !== 'object') {
        return { source: 'the directory tree', sections: [], notes: list() };
    }
    const record = config;
    const space = typeof record['name'] === 'string' ? record['name'] : undefined;
    if (space)
        note('derived', 'site-title', 'The space name came from `name` in the Mintlify config.');
    const navigation = record['navigation'];
    const sections = readNavigation(navigation, note);
    if (sections.length === 0) {
        note('unconverted', 'navigation-unreadable', 'No navigation groups could be read from the Mintlify config. The directory tree was used instead.');
        return { ...(space ? { space } : {}), source: 'the directory tree', sections, notes: list() };
    }
    return { ...(space ? { space } : {}), source: 'docs.json', sections, notes: list() };
}
function readNavigation(navigation, note) {
    // mint.json: a flat array of groups.
    if (Array.isArray(navigation)) {
        return navigation.flatMap((group) => readGroup(group, note));
    }
    if (navigation === null || typeof navigation !== 'object')
        return [];
    const record = navigation;
    // docs.json: one of these keys holds the tree. `versions` and `languages`
    // duplicate the whole tree, which we have no equivalent for.
    for (const key of ['versions', 'languages']) {
        const value = record[key];
        if (!Array.isArray(value) || value.length === 0)
            continue;
        note('unconverted', `navigation-${key}`, `The site had several ${key}, each with its own page tree. We have no versioning or translation, so only the FIRST was read and the rest were left out.`);
        return readNavigation(value[0], note);
    }
    for (const key of ['tabs', 'dropdowns', 'anchors']) {
        const value = record[key];
        if (!Array.isArray(value) || value.length === 0)
            continue;
        note('review', `navigation-${key}`, `The site used ${key}, which sit above sections in Mintlify and have no equivalent here. Each one became a section, and its groups became nested items inside it — one level deeper than the source.`);
        return value.flatMap((entry) => {
            if (entry === null || typeof entry !== 'object')
                return [];
            const tab = entry;
            const label = typeof tab['tab'] === 'string'
                ? tab['tab']
                : typeof tab['dropdown'] === 'string'
                    ? tab['dropdown']
                    : typeof tab['anchor'] === 'string'
                        ? tab['anchor']
                        : 'Section';
            const inner = readNavigation(tab, note);
            if (inner.length === 0) {
                const pages = readPages(tab['pages'], note);
                return pages.length > 0 ? [{ label, items: pages }] : [];
            }
            return [
                {
                    label,
                    items: inner.map((section) => ({ label: section.label, items: section.items })),
                },
            ];
        });
    }
    if (Array.isArray(record['groups'])) {
        return record['groups'].flatMap((group) => readGroup(group, note));
    }
    if (Array.isArray(record['pages'])) {
        const pages = readPages(record['pages'], note);
        return pages.length > 0 ? [{ label: 'Overview', items: pages }] : [];
    }
    return [];
}
function readGroup(group, note) {
    if (typeof group === 'string') {
        return [{ label: 'Overview', items: [{ page: mintlifyPageId(group) }] }];
    }
    if (group === null || typeof group !== 'object')
        return [];
    const record = group;
    const label = typeof record['group'] === 'string' ? record['group'] : 'Section';
    const items = readPages(record['pages'], note);
    return items.length > 0 ? [{ label, items }] : [];
}
function readPages(pages, note) {
    if (!Array.isArray(pages))
        return [];
    return pages.flatMap((page) => {
        if (typeof page === 'string')
            return [{ page: mintlifyPageId(page) }];
        if (page === null || typeof page !== 'object')
            return [];
        const record = page;
        if (typeof record['group'] === 'string') {
            return [{ label: record['group'], items: readPages(record['pages'], note) }];
        }
        if (typeof record['href'] === 'string') {
            note('unconverted', 'nav-external-link', 'A navigation entry pointed at an external URL. Our navigation holds pages only, so it was left out and needs adding by hand.');
            return [];
        }
        if (typeof record['openapi'] === 'string') {
            note('unconverted', 'nav-openapi', 'A navigation entry generated its pages from an OpenAPI document. We do not read OpenAPI yet, so those pages are not in the proposal and are not imported.');
            return [];
        }
        return [];
    });
}
function str(value) {
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}
// ------------------------------------------------------------- redirects
/**
 * The redirects a Mintlify site was already serving.
 *
 * `docs.json` carries a `redirects` array — every old address the customer has
 * already promised to keep working, usually accumulated over years of moving
 * pages around. Nothing read it, so a migration silently dropped the lot and
 * every one of those addresses started answering 404 the day the new site went
 * live. That is the worst possible moment for it: the links are in other
 * people's blog posts, in changelogs, in support replies nobody can edit.
 *
 * Refused rather than repaired when a rule is not something this product can
 * express — a note says which and why, because a redirect quietly reshaped is
 * worse than one somebody knows to write by hand.
 */
export function mintlifyRedirects(source) {
    const { note, list } = createNotes();
    let config;
    try {
        config = JSON.parse(source);
    }
    catch {
        // The navigation reader says this too; saying it twice in one report is
        // worse than saying it once, so this stays quiet and lets that one speak.
        return { redirects: [], notes: list() };
    }
    if (config === null || typeof config !== 'object')
        return { redirects: [], notes: list() };
    const raw = config['redirects'];
    if (!Array.isArray(raw))
        return { redirects: [], notes: list() };
    const redirects = [];
    for (const entry of raw) {
        if (entry === null || typeof entry !== 'object')
            continue;
        const rule = entry;
        const from = typeof rule['source'] === 'string' ? rule['source'].trim() : '';
        const to = typeof rule['destination'] === 'string' ? rule['destination'].trim() : '';
        if (from === '' || to === '')
            continue;
        /**
         * Mintlify allows `:param` and `:splat*` in a source. Ours allows a single
         * trailing `*`, which covers the common "a whole section moved" case and
         * nothing else — so a rule with a named parameter is reported rather than
         * bent into a shape that would send readers somewhere they did not ask for.
         */
        if (/:[A-Za-z]/.test(from) || /:[A-Za-z]/.test(to)) {
            note('unconverted', 'redirect-parameter', 'A redirect used a named parameter (`:slug`), which our redirects cannot express. It was left out rather than approximated, and needs writing by hand.');
            continue;
        }
        if (from.includes('*') && !from.endsWith('*')) {
            note('unconverted', 'redirect-wildcard', 'A redirect had a wildcard in the middle of its path, which only matches at the end here. It was left out rather than approximated.');
            continue;
        }
        // `permanent: false` is a 302; anything else is the 301 both products
        // default to. Written only when it is not the default — a file full of
        // `status: 301` hides the one deliberate 302.
        const permanent = rule['permanent'];
        redirects.push({
            from: from.startsWith('/') ? from : `/${from}`,
            to: to.startsWith('/') || /^https?:\/\//i.test(to) ? to : `/${to}`,
            ...(permanent === false ? { status: 302 } : {}),
        });
    }
    return { redirects, notes: list() };
}
//# sourceMappingURL=mintlify.js.map